import { Link } from 'react-router-dom';
import { Card, CardTitle, CardDescription } from '@/components/ui/card';
import { Tag } from '@/components/common/Tag';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { useEntities } from '@/lib/queries';
import type { EntityDto, EntityCategory } from '@/types/dto';

const categoryLabels: Record<EntityCategory, string> = {
  Contractor: 'Drilling Contractors',
  Operator: 'Operators (E&P)',
  Competitor: 'Competitors & Tech',
  Partner: 'Suppliers & Partners',
  Industry: 'Industry / Regulators',
  Publication: 'Trade Publications',
};

const categoryOrder: EntityCategory[] = [
  'Contractor',
  'Operator',
  'Competitor',
  'Partner',
  'Industry',
  'Publication',
];

export default function Entities() {
  const { data, isLoading, error } = useEntities();

  return (
    <>
      <Card>
        <CardTitle>Tracked Entities</CardTitle>
        <CardDescription className="mt-2">
          Drilling-digital ecosystem: contractors, operators, competitors, partners, industry bodies, and trade publications.
        </CardDescription>
      </Card>

      {isLoading && <LoadingSkeleton lines={8} />}
      {error && <ErrorState message={(error as Error).message} />}

      {data && categoryOrder.map((cat) => {
        const group = data
          .filter((e) => e.category === cat)
          .sort((a, b) => a.tier - b.tier || a.name.localeCompare(b.name));
        if (group.length === 0) return null;
        return (
          <Card key={cat}>
            <div className="flex items-baseline gap-2 mb-4">
              <h2 className="text-2xl font-semibold">{categoryLabels[cat]}</h2>
              <span className="text-muted text-sm">({group.length})</span>
            </div>
            {/* Desktop table */}
            <table className="hidden md:table w-full text-sm">
              <thead>
                <tr className="border-b-2 border-accent">
                  <th style={{ width: 60 }} className="py-2 px-3 text-left text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)">Tier</th>
                  <th className="py-2 px-3 text-left text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)">Name</th>
                  <th className="py-2 px-3 text-left text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)">Website</th>
                  <th className="py-2 px-3 text-left text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)">LinkedIn</th>
                  <th className="py-2 px-3 text-left text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)">Note</th>
                </tr>
              </thead>
              <tbody>
                {group.map((e) => <EntityRowDesktop key={e.id} e={e} />)}
              </tbody>
            </table>

            {/* Mobile cards */}
            <ul className="md:hidden flex flex-col divide-y divide-border">
              {group.map((e) => <EntityRowMobile key={e.id} e={e} />)}
            </ul>
          </Card>
        );
      })}
    </>
  );
}

function tierVariant(tier: number): 'tier-1' | 'tier-2' | 'tier-3' {
  return tier === 1 ? 'tier-1' : tier === 2 ? 'tier-2' : 'tier-3';
}

function EntityRowDesktop({ e }: { e: EntityDto }) {
  return (
    <tr className="border-b border-border hover:bg-surface-hover align-top">
      <td className="py-3 px-3"><Tag variant={tierVariant(e.tier)}>T{e.tier}</Tag></td>
      <td className="py-3 px-3">
        <Link to={`/hmhwatch/entities/${e.id}`} className="text-accent hover:underline font-medium">{e.name}</Link>
      </td>
      <td className="py-3 px-3">
        {e.websiteUrl && (
          <a href={e.websiteUrl} target="_blank" rel="noopener" className="text-accent hover:underline">
            {hostOf(e.websiteUrl)}
          </a>
        )}
      </td>
      <td className="py-3 px-3">
        {e.linkedInUrl && (
          <a href={e.linkedInUrl} target="_blank" rel="noopener" className="text-accent hover:underline">
            {e.linkedInSlug}
          </a>
        )}
      </td>
      <td className="py-3 px-3 text-muted text-xs">{e.note}</td>
    </tr>
  );
}

function EntityRowMobile({ e }: { e: EntityDto }) {
  return (
    <li className="py-3">
      <div className="flex items-start gap-3">
        <Tag variant={tierVariant(e.tier)} className="flex-shrink-0">T{e.tier}</Tag>
        <div className="min-w-0 flex-1">
          <Link to={`/hmhwatch/entities/${e.id}`} className="text-accent hover:underline font-medium">{e.name}</Link>
          <div className="text-[0.75rem] text-muted mt-1 flex flex-wrap gap-x-2">
            {e.linkedInUrl && (
              <a href={e.linkedInUrl} target="_blank" rel="noopener" className="hover:underline">LinkedIn</a>
            )}
            {e.websiteUrl && (
              <>
                {e.linkedInUrl && <span>·</span>}
                <a href={e.websiteUrl} target="_blank" rel="noopener" className="hover:underline">
                  {hostOf(e.websiteUrl)}
                </a>
              </>
            )}
          </div>
          {e.note && <p className="text-muted text-xs mt-1">{e.note}</p>}
        </div>
      </div>
    </li>
  );
}

function hostOf(url: string): string {
  try { return new URL(url).host; } catch { return url; }
}
